import { useEffect, useState } from 'react'
import {
  collection,
  getCountFromServer,
  query,
  type QueryConstraint,
} from 'firebase/firestore'
import { db } from '../firebase'

/**
 * useServerCounts — TRUE totals for a client subcollection, via Firestore count()
 * aggregation (2026-10-09).
 *
 * ⚠️ THE CLASS: a list subscription carries a `limit(N)` so the page stays fast, and
 * then `rows.length` (or `rows.filter(...).length`) gets shown to the client as a
 * total. It reads correctly until the client crosses N, and then silently freezes.
 * CPA Jake was told he had 100 customers when he had 1,585; his "Skill Tasks" read
 * 80 of 196, because both were lengths of capped reads. A count() aggregation costs
 * one read per 1,000 matched entries and never caps.
 *
 * `specs` maps a key to the constraints that define it. It MUST be a stable
 * reference (module constant or useMemo) — it is the effect dependency.
 * A failed or not-yet-returned count is `null`; callers decide the fallback and
 * must never present a capped length as if it were the count.
 */
export function useServerCounts<K extends string>(
  parentCollection: string,
  parentId: string | null | undefined,
  subcollection: string,
  specs: Readonly<Record<K, readonly QueryConstraint[]>>,
  refreshKey: unknown = 0,
): Record<K, number | null> {
  const keys = Object.keys(specs) as K[]
  const empty = () => Object.fromEntries(keys.map((k) => [k, null])) as Record<K, number | null>
  const [counts, setCounts] = useState<Record<K, number | null>>(empty)

  useEffect(() => {
    if (!parentId) { setCounts(empty()); return }
    let cancelled = false
    const col = collection(db, `${parentCollection}/${parentId}/${subcollection}`)
    void Promise.all(keys.map(async (k) => {
      try {
        const snap = await getCountFromServer(query(col, ...specs[k]))
        return [k, snap.data().count] as const
      } catch {
        return [k, null] as const
      }
    })).then((pairs) => {
      if (!cancelled) setCounts(Object.fromEntries(pairs) as Record<K, number | null>)
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- specs is a stable reference by contract
  }, [parentCollection, parentId, subcollection, specs, refreshKey])

  return counts
}
